import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useVenue } from "../lib/venue";
import { getVenueEventsWithClosing, joinMailingList } from "../lib/api";
import { isEventLive } from "../lib/events";
import { mergeCarouselItems } from "../lib/carousel-merge";
import { formatEventShortDate } from "../lib/venue-format";
import { LiveEventStrip } from "./live-event-strip";
import { pushCtaClick, pushFormInteraction, pushNavigation, toSnakeCaseLabel, type CtaLabel } from "../lib/analytics";
import { resolveFooter, type FooterSocials } from "../lib/site-footer";
import { faqLayoutFor, isPageLive, pageForPath, type FreeformItem, type SiteBlock, type SitePage } from "../lib/site-pages";
import {
  usePreviewOverlay,
  useInAdminPreview,
  PREVIEW_EDIT_MSG_TYPE,
  PREVIEW_REORDER_MSG_TYPE,
  PREVIEW_DELETE_MSG_TYPE,
  PREVIEW_SELECT_MSG_TYPE,
  PREVIEW_GALLERY_REORDER_MSG_TYPE,
  PREVIEW_ITEM_REORDER_MSG_TYPE,
  type PreviewEditMessage,
  type PreviewReorderMessage,
  type PreviewDeleteMessage,
  type PreviewSelectMessage,
  type PreviewGalleryReorderMessage,
  type PreviewItemReorderMessage,
} from "../lib/preview-overlay";
import { SectionAdminOverlay, BlockIndexContext, useBlockIndex } from "./admin-preview-overlay";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BLOCK_LABELS } from "../lib/site-pages";

// Inline SVG paths for social icons in the editorial footer (Instagram +
// Facebook only, matching the reference; other socials fall back to their
// name in uppercase kicker style).
const FOOTER_SOCIAL_PATHS: Partial<Record<keyof FooterSocials, string>> = {
  instagram:
    "M12 2.2c3.2 0 3.6 0 4.8.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.7 3.7 0 0 1-.9 1.38 3.7 3.7 0 0 1-1.38.9c-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.6 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2m0 5.4a4.4 4.4 0 1 0 0 8.8 4.4 4.4 0 0 0 0-8.8m0 7.26a2.86 2.86 0 1 1 0-5.72 2.86 2.86 0 0 1 0 5.72m5.6-7.44a1.03 1.03 0 1 1-2.06 0 1.03 1.03 0 0 1 2.06 0",
  facebook:
    "M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12",
};

// Editorial four-column footer for the white-label site (addresses, legal
// links, mailing list, social icons). Rendered inline in SitePagesSite so it
// picks up the same tokens/type as the rest of the site.
function EditorialFooter({
  venueId,
  copyright,
  addresses,
  links,
  socials,
  mailingListHeading,
  mailingListId,
  showMailingList,
  footerPages,
  hrefFor,
}: {
  venueId: string;
  copyright: string;
  addresses: Array<{ label?: string; lines: string[] }>;
  links: Array<{ label: string; href: string }>;
  socials: Array<[keyof FooterSocials, string]>;
  mailingListHeading: string;
  mailingListId?: string;
  showMailingList: boolean;
  footerPages: SitePage[];
  hrefFor: (p: SitePage) => string;
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === "sending") return;
    pushCtaClick("subscribe");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setState("error");
      return;
    }
    setState("sending");
    try {
      await joinMailingList(venueId, email.trim(), "footer", undefined, mailingListId);
      pushFormInteraction("success");
      setState("done");
      setEmail("");
    } catch {
      pushFormInteraction("fail");
      setState("error");
    }
  };

  const hasColumns = links.length > 0 || addresses.length > 0 || showMailingList;
  return (
    <footer className="px-6 pb-10 pt-16" style={{ borderTop: `1px solid ${HAIR}` }}>
      {hasColumns && (
        <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Legal / info links */}
          {links.length > 0 && (
            <nav className="flex flex-col gap-2 text-[13px]">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target={l.href.startsWith("http") ? "_blank" : undefined}
                  rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="underline decoration-1 underline-offset-4 transition hover:opacity-70"
                  style={{ color: "var(--venue-text)" }}
                >
                  {l.label}
                </a>
              ))}
            </nav>
          )}
          {/* Addresses (one column each) */}
          {addresses.map((a, i) => (
            <div key={i} className="text-[13px] leading-relaxed">
              {a.label && (
                <div className="mb-3 text-[13px] font-extrabold uppercase tracking-tight" style={{ color: "var(--venue-text)" }}>
                  {a.label}
                </div>
              )}
              <address className="not-italic" style={{ color: MUTED }}>
                {a.lines.map((line, j) => (
                  <div key={j}>{line}</div>
                ))}
              </address>
            </div>
          ))}
          {/* Mailing list */}
          {showMailingList && (
            <div className="text-[13px]">
              <div className="mb-3 font-extrabold uppercase tracking-tight" style={{ color: "var(--venue-text)" }}>{mailingListHeading}</div>
              {state === "done" ? (
                <p style={{ color: MUTED }}>Thanks, you're on the list.</p>
              ) : (
                <form onSubmit={submit} noValidate className="flex overflow-hidden" style={{ border: `1px solid ${HAIR}` }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Subscribe"
                    aria-label="Email address"
                    className="min-w-0 flex-1 bg-transparent px-3 py-2 text-[13px] outline-none placeholder:opacity-60"
                    style={{ color: "var(--venue-text)" }}
                  />
                  <button
                    type="submit"
                    disabled={state === "sending"}
                    aria-label="Subscribe"
                    className="flex items-center border-0 px-4 text-[13px] font-bold transition hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
                  >
                    {state === "sending" ? "…" : "→"}
                  </button>
                </form>
              )}
              {state === "error" && <p className="mt-1 text-xs" style={{ color: "var(--accent)" }}>Couldn't subscribe, try again.</p>}
            </div>
          )}
        </div>
      )}

      {/* Bottom bar: copyright + footer legal-page shortcuts + social icons. */}
      <div
        className={`mx-auto ${hasColumns ? "mt-12 border-t pt-6" : ""} flex max-w-6xl flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between`}
        style={hasColumns ? { borderColor: HAIR } : undefined}
      >
        <p className="text-[11px] tracking-[0.14em]" style={{ color: MUTED }}>{copyright}</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {footerPages.map((p) => (
            <a key={p.slug} href={hrefFor(p)} className="text-[10px] uppercase tracking-[0.2em] transition hover:opacity-70" style={{ color: MUTED }}>
              {p.title}
            </a>
          ))}
          {socials.map(([k, url]) => {
            const path = FOOTER_SOCIAL_PATHS[k];
            return path ? (
              <a
                key={k}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={k}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:opacity-70"
                style={{ backgroundColor: "var(--venue-text)", color: "var(--venue-bg)" }}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
                  <path d={path} />
                </svg>
              </a>
            ) : (
              <a key={k} href={url} target="_blank" rel="noopener noreferrer" className={NAV_LABEL}>{k}</a>
            );
          })}
        </div>
      </div>
    </footer>
  );
}

// Multi-page white-label site renderer, EDITORIAL direction (the Certs/Keo
// language, adapted to each venue's Branding): no app-style top bar, a thin
// floating type-only nav over a full-bleed hero, the venue name as a huge
// wordmark, sharp edges, big display headings, punchy edge-to-edge imagery.
// Config (pages/blocks) unchanged, this is pure presentation.

const HAIR = "color-mix(in srgb, var(--venue-text) 16%, transparent)";
const MUTED = "color-mix(in srgb, var(--venue-text) 64%, transparent)";
const NAV_LABEL = "text-[10px] font-bold uppercase tracking-[0.26em] transition hover:opacity-60 active:opacity-60";
const KICKER = "text-[11px] font-bold uppercase tracking-[0.3em]";
const WRAP = "mx-auto max-w-6xl px-6";
// Explicit (not inherited) so display headings keep the venue's regular font
// even when appConfig.secondaryFontFamily overrides the base/body font below.
const DISPLAY: React.CSSProperties = {
  letterSpacing: "-0.01em",
  fontFamily: "var(--venue-font, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Helvetica, Arial, sans-serif)",
};
// The venue-name wordmark gets its own slab-serif brand face + accent colour
// (mixed case, not the site's usual uppercase sans), a distinct "logotype"
// treatment vs. the rest of the editorial type system.
const WORDMARK_FONT: React.CSSProperties = { fontFamily: '"Bevan", serif', letterSpacing: "-0.01em" };
// Fractal-noise SVG, tiled, a cheap "film grain" texture with no image asset
// to host. Sits over photos only (never over text) via layer order + blend mode.
const GRAIN_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/** Crackly film-grain layer for a photo, always BELOW nav/heading z-index, never over text. */
// fadeBottom: for hero banners, where the grain sits over a photo that
// vignettes down into flat page background, without this the grain's own
// hard-edged box reintroduces exactly the seam the vignette was fixing
// (crackle above the line, dead flat below it). Plain tiles (gallery/imageText)
// don't need it, their edges are the image's own edges, not a blend point.
function GrainOverlay({ fadeBottom }: { fadeBottom?: boolean } = {}) {
  const mask = fadeBottom ? "linear-gradient(to bottom, black, black 60%, transparent 100%)" : undefined;
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: GRAIN_BG,
        backgroundSize: "140px 140px",
        mixBlendMode: "overlay",
        opacity: 0.7,
        ...(mask ? { WebkitMaskImage: mask, maskImage: mask } : {}),
      }}
      aria-hidden
    />
  );
}


/** Subtle vintage-rave accents (purple ticks + a red spotlight beam/discs),
    Love Inn's own poster language, kept sparse so it reads as texture, not clutter. */
function HeroAccents() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <span style={{ position: "absolute", top: "9%", left: "5%", width: 2, height: 26, borderRadius: 2, background: "#c583ff", opacity: 0.7, transform: "rotate(9deg)" }} />
      <span style={{ position: "absolute", top: "7%", left: "31%", width: 2, height: 18, borderRadius: 2, background: "#c583ff", opacity: 0.55, transform: "rotate(-8deg)" }} />
      <span style={{ position: "absolute", bottom: "16%", left: "8%", width: 2, height: 24, borderRadius: 2, background: "#c583ff", opacity: 0.5, transform: "rotate(6deg)" }} />
      <div
        style={{
          position: "absolute",
          top: -10,
          right: "16%",
          width: 64,
          height: 200,
          opacity: 0.4,
          filter: "blur(4px)",
          background: "linear-gradient(to top, rgba(255,90,60,0.85), rgba(255,90,60,0))",
          clipPath: "polygon(46% 100%, 54% 100%, 100% 0%, 0% 0%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -10,
          left: "42%",
          width: 46,
          height: 150,
          opacity: 0.3,
          filter: "blur(4px)",
          background: "linear-gradient(to top, rgba(255,90,60,0.85), rgba(255,90,60,0))",
          clipPath: "polygon(46% 100%, 54% 100%, 100% 0%, 0% 0%)",
          transform: "rotate(-4deg)",
        }}
      />
      <div style={{ position: "absolute", top: "5%", right: "12%", width: 58, height: 20, borderRadius: "50%", filter: "blur(1.5px)", background: "radial-gradient(closest-side, rgba(255,120,90,0.55), transparent)" }} />
      <div style={{ position: "absolute", top: "13%", right: "21%", width: 38, height: 14, borderRadius: "50%", filter: "blur(1.5px)", background: "radial-gradient(closest-side, rgba(255,205,150,0.5), transparent)" }} />
      <div style={{ position: "absolute", top: "24%", right: "6%", width: 46, height: 17, borderRadius: "50%", filter: "blur(1.5px)", background: "radial-gradient(closest-side, rgba(255,120,90,0.45), transparent)" }} />
      <div style={{ position: "absolute", top: "34%", left: "44%", width: 40, height: 15, borderRadius: "50%", filter: "blur(1.5px)", background: "radial-gradient(closest-side, rgba(255,120,90,0.4), transparent)" }} />
      <div style={{ position: "absolute", bottom: "22%", right: "8%", width: 50, height: 18, borderRadius: "50%", filter: "blur(1.5px)", background: "radial-gradient(closest-side, rgba(255,140,100,0.4), transparent)" }} />
    </div>
  );
}

// Event poster with an onError fallback to the venue logo. Third-party poster
// URLs (HeadFirst, promoter CDNs) rot without warning; without this, a 404 or
// hotlink-block paints a broken-image icon on the venue's home page. Falls
// through: poster → tinted venue logo → tinted empty tile.
function PosterImage({
  src,
  alt,
  venueLogoUrl,
  venueName,
}: {
  src?: string;
  alt: string;
  venueLogoUrl: string | null;
  venueName: string;
}) {
  const [errored, setErrored] = useState(false);
  const showFallback = !src || errored;
  if (!showFallback) {
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setErrored(true)}
        className="h-full w-full object-cover"
        style={{ filter: "contrast(1.06) saturate(1.12)" }}
      />
    );
  }
  if (venueLogoUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6" style={{ backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)" }}>
        <img src={venueLogoUrl} alt={venueName} className="max-h-full max-w-full object-contain opacity-80" />
      </div>
    );
  }
  return <div className="h-full w-full" style={{ backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)" }} />;
}

function useWordmarkFont() {
  useGoogleFont("Bevan");
}

// Loads a Google Fonts family on demand (e.g. appConfig.secondaryFontFamily).
// Keyed by family name so switching venues/fonts doesn't skip a real load.
function useGoogleFont(family?: string) {
  useEffect(() => {
    if (!family) return;
    const id = `site-pages-font-${family.replace(/\s+/g, "-").toLowerCase()}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}&display=swap`;
    document.head.appendChild(link);
  }, [family]);
}

export function SitePagesSite({ pathname }: { pathname: string }) {
  const { data } = useVenue();
  const venue = data?.venue;
  const cfg = venue?.appConfig?.sitePages;
  // Inside the admin preview iframe (usePreviewOverlay returns a non-null
  // draft), draft pages MUST render so operators can build them before
  // publishing. On the public site (overlay is null) drafts are hidden from
  // nav/footer/direct-URL access. Tyler @ Toka flagged the missing draft
  // state 2026-09-10.
  const inPreview = usePreviewOverlay() !== null;
  const page =
    pageForPath(cfg, pathname, { includeDrafts: inPreview }) ??
    (cfg?.pages?.find((p) => inPreview || isPageLive(p)) ?? null);
  const [menuOpen, setMenuOpen] = useState(false);
  const secondaryFont = venue?.appConfig?.secondaryFontFamily;
  const hasOutlineHero = page?.blocks?.some((b) => b.type === "hero" && b.headingStyle === "outline") ?? false;
  const splashBlock = page?.blocks?.find((b) => b.type === "splash") as Extract<SiteBlock, { type: "splash" }> | undefined;
  useWordmarkFont();
  useGoogleFont(secondaryFont);
  // Bold geometric grotesk for the "outline" hero heading variant, tight
  // kerning, perfectly circular counters. Only loaded where actually used.
  useGoogleFont(hasOutlineHero ? "Poppins:wght@900" : undefined);
  // Only fetch from Google Fonts when there's no custom URL. A hosted file
  // (paid foundry font like BHH Bartle) is injected via @font-face in the
  // block itself, doesn't need a Google Fonts request.
  useGoogleFont(splashBlock?.headingFontUrl ? undefined : (splashBlock?.headingFont ?? (splashBlock ? "Archivo Black" : undefined)));
  useGoogleFont(splashBlock?.bodyFontUrl ? undefined : (splashBlock?.bodyFont ?? (splashBlock ? "Space Mono" : undefined)));

  // Body scroll is locked while the drawer is open so the page behind it
  // doesn't scroll under the backdrop; Escape closes it too.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  if (!venue || !page) return null;
  // Public visitors see LIVE pages only. In the admin preview iframe every
  // page (including drafts) is visible so operators can preview nav + footer
  // as they'll appear after publish.
  const pages = cfg!.pages.filter((p) => inPreview || isPageLive(p));
  // Menu order = pages marked `inNav`, in their site-pages array position.
  // The "Menu order" drag panel in the editor mutates that array position, so
  // this preserves 1:1 what the operator sees there (Tyler @ Toka 2026-09-23:
  // the old primary-first-then-nav split silently reshuffled the menu so the
  // menu never matched the drag order). Primary pages remain always-visible
  // as their own button elsewhere; when they're also `inNav` they take their
  // array position here rather than forcibly leading the list.
  const menuPages = pages.filter((p) => p.inNav);
  const footerPages = pages.filter((p) => p.footer);
  const footer = resolveFooter(venue, venue.appConfig?.footer, new Date());
  const socials = Object.entries(footer?.socials ?? {});
  const href = (p: SitePage) => (p.slug ? `/${p.slug}` : "/");
  const name = venue.appName || venue.name;
  const logoUrl = venue.appConfig?.wordmarkLogoUrl;
  const isHome = (page.slug ?? "") === "";
  const heroLeads = page.blocks[0]?.type === "hero";

  // Small logo, inline with the burger button, same row, both breakpoints
  // (was previously stacked on its own line below the nav, out of line
  // with it).
  const compactLogo = (
    <a href="/" className="relative z-20 block">
      {logoUrl ? (
        <img src={logoUrl} alt={name} className="inline-block object-contain" style={{ height: "clamp(1.1rem, 2.6vw, 1.6rem)", width: "auto" }} />
      ) : (
        <span style={{ ...WORDMARK_FONT, fontSize: "clamp(1.1rem, 2.6vw, 1.6rem)", color: "var(--accent)" }}>{name}</span>
      )}
    </a>
  );
  const navRow = (
    <div className="relative z-30 flex items-center justify-between px-6 py-5">
      {compactLogo}
      <BurgerButton
        open={menuOpen}
        onClick={() =>
          setMenuOpen((o) => {
            const next = !o;
            if (next) pushNavigation("main_menu");
            return next;
          })
        }
      />
    </div>
  );

  const menuOverlay = (
    <>
      {/* Backdrop, click to dismiss */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${menuOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
        style={{ backgroundColor: "color-mix(in srgb, var(--venue-bg) 55%, black 15%)" }}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />
      {/* Slide-out drawer */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex w-[86vw] max-w-sm flex-col p-8 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${menuOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ backgroundColor: "var(--venue-bg)", color: "var(--venue-text)", borderLeft: `1px solid ${HAIR}` }}
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
      >
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.26em]" style={{ color: MUTED }}>Menu</span>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="group flex h-9 w-9 items-center justify-center border-0 bg-transparent p-0 shadow-none transition-transform duration-150 hover:translate-y-0 hover:bg-transparent hover:shadow-none active:scale-90"
            style={{ color: MUTED }}
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="mt-14 flex flex-col gap-1">
          {menuPages.map((p, i) => (
            <a
              key={p.slug}
              href={href(p)}
              onClick={() => pushNavigation(toSnakeCaseLabel(p.title))}
              className="group py-2.5 text-3xl font-extrabold uppercase leading-tight tracking-tight transition-all duration-300 active:opacity-60"
              style={{
                ...DISPLAY,
                transitionProperty: "transform, opacity",
                transform: menuOpen ? "translateX(0)" : "translateX(12px)",
                opacity: menuOpen ? 1 : 0,
                transitionDelay: menuOpen ? `${80 + i * 40}ms` : "0ms",
              }}
            >
              <span className="relative inline-block transition-transform duration-200 ease-out group-hover:translate-x-1.5 group-active:translate-x-1.5">
                {p.title}
                <span
                  className="absolute -bottom-0.5 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100 group-active:scale-x-100"
                  style={{ backgroundColor: "var(--accent)" }}
                  aria-hidden
                />
              </span>
            </a>
          ))}
        </nav>
        <div className="mt-auto flex flex-wrap gap-x-6 gap-y-2 pt-8">
          {socials.map(([k, url]) => (
            <a key={k} href={url} target="_blank" rel="noopener noreferrer" className={NAV_LABEL}>{k}</a>
          ))}
        </div>
      </div>
    </>
  );

  // Bare page (single-purpose splash): no site chrome, no nav, no menu, no
  // footer. Rendered edge-to-edge so the splash block owns the viewport.
  if (page.bare) {
    return (
      <div className="min-h-[100dvh]" style={{ backgroundColor: "var(--venue-bg)", color: "var(--venue-text)" }}>
        {page.blocks.map((b, i) => (
          <BlockAdminOverlay key={i} block={b} blockIndex={i} totalBlocks={page.blocks.length}>
            <Block block={b} venueId={venue.id} venueName={name} venueLogoUrl={venue.appIconUrl || venue.imageUrl || null} />
          </BlockAdminOverlay>
        ))}
      </div>
    );
  }

  return (
    <div
      className="min-h-[100dvh]"
      style={{
        backgroundColor: "var(--venue-bg)",
        color: "var(--venue-text)",
        // Secondary/small text (nav, footer, captions, body copy, anything
        // not using DISPLAY/WORDMARK_FONT above) picks this up by inheritance;
        // headings stay on the venue's regular font via DISPLAY's own explicit
        // fontFamily. Falls back to "inherit" (a no-op) when unset.
        fontFamily: secondaryFont ? `"${secondaryFont}", sans-serif` : "inherit",
      }}
    >
      {menuOverlay}

      {heroLeads ? (
        // Hero page: nav floats OVER the hero image, no bar.
        <HeroLead block={page.blocks[0] as Extract<SiteBlock, { type: "hero" }>} nav={navRow} isHome={isHome} name={name} logoUrl={logoUrl} />
      ) : (
        <header>{navRow}</header>
      )}

      {/* Subpages only: kill the first block's own top padding so it sits
          right under the (now much shorter) compact hero instead of
          leaving a second, redundant gap on top of the hero's own. Home
          keeps its normal spacing under the full-height wordmark hero. */}
      <main className={heroLeads && !isHome ? "[&>*:first-child]:!pt-6" : undefined}>
        {page.blocks.slice(heroLeads ? 1 : 0).map((b, i) => {
          // `i` here is 0-based inside the sliced array. `realIndex` is the
          // block's position in the un-sliced page.blocks list, which is what
          // the admin overlay + parent editor need for reorder/delete/select.
          const realIndex = heroLeads ? i + 1 : i;
          return (
            <BlockAdminOverlay key={realIndex} block={b} blockIndex={realIndex} totalBlocks={page.blocks.length}>
              <Block block={b} venueId={venue.id} venueName={name} venueLogoUrl={venue.appIconUrl || venue.imageUrl || null} />
            </BlockAdminOverlay>
          );
        })}
      </main>

      <SectionAdminOverlay sectionId="site-footer" label="site footer">
        <EditorialFooter
          venueId={venue.id}
          copyright={footer.copyright}
          addresses={footer.addresses}
          links={footer.links}
          socials={socials as Array<[keyof FooterSocials, string]>}
          mailingListHeading={footer.mailingListHeading}
          mailingListId={footer.mailingListId}
          showMailingList={footer.showMailingList}
          footerPages={footerPages}
          hrefFor={href}
        />
      </SectionAdminOverlay>
    </div>
  );
}

// Burger icon that morphs into an X as the drawer opens, the two outer bars
// rotate/translate into place, the middle bar fades, all in one motion.
function BurgerButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  // Bumped from h-10 w-10 (bars w-6, h-[13px]) to h-12 w-12 (bars w-7,
  // h-[15px]) per Tyler @ Toka 2026-09-23: the old icon read as tiny against
  // the widescreen hero, especially on a large monitor. Same morph geometry,
  // just proportionally larger.
  const bar = "absolute left-0 h-[2px] w-7 transition-all duration-300 ease-[cubic-bezier(0.65,0,0.35,1)]";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={open ? "Close menu" : "Open menu"}
      className="relative z-40 flex h-12 w-12 items-center justify-center border-0 bg-transparent p-0 shadow-none transition-transform duration-150 hover:translate-y-0 hover:bg-transparent hover:shadow-none active:scale-90"
      style={{ color: "var(--venue-text)" }}
    >
      <span className="relative block h-[15px] w-7" aria-hidden>
        <span className={bar} style={{ top: 0, backgroundColor: "currentColor", transform: open ? "translateY(6.5px) rotate(45deg)" : "none" }} />
        <span className={`${bar} top-1/2 -translate-y-1/2 transition-opacity duration-150`} style={{ backgroundColor: "currentColor", opacity: open ? 0 : 1 }} />
        <span className={bar} style={{ bottom: 0, backgroundColor: "currentColor", transform: open ? "translateY(-6.5px) rotate(-45deg)" : "none" }} />
      </span>
    </button>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform duration-300 ease-out group-hover:rotate-90 group-active:rotate-90" aria-hidden>
      <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// The lead hero's two CTA slots are free-text label + free-href per venue
// (admin-configured, e.g. Love Inn's cta2 is "Subscribe" -> "/subscribe",
// but cta1 is "What's on" -> "/whats-on"), there's no "kind" field to key
// tracking off, so this maps the small set of hrefs we actually care about
// to our canonical cta_click label. Anything else (a generic nav link) is
// intentionally left untracked rather than guessed at.
const APP_STORE_HOSTS = ["apps.apple.com", "play.google.com", "testflight.apple.com"];

function heroCtaLabel(href: string): CtaLabel | null {
  if (href === "/subscribe" || href.startsWith("/subscribe?") || href.startsWith("/subscribe#")) return "subscribe";
  if (APP_STORE_HOSTS.some((host) => href.includes(host))) return "download";
  return null;
}

// Hero with the nav floating over it, the logo lives in the nav row
// (inline with the burger) for every page now. Home = full viewport with
// the giant wordmark; inner pages = a punchy banner with the page heading.
function HeroLead({
  block: b,
  nav,
  isHome,
  name,
  logoUrl,
}: {
  block: Extract<SiteBlock, { type: "hero" }>;
  nav: React.ReactNode;
  isHome: boolean;
  name: string;
  logoUrl?: string;
}) {
  // Same fixed footprint for both CTAs regardless of label length, equal
  // width/height, 8px corners, so the pair always reads as one control.
  const ctaBase = "flex h-12 min-w-[176px] items-center justify-center rounded-lg px-6 text-[11px] font-bold uppercase tracking-[0.26em] transition";
  const centered = b.align === "center";
  // Existing behaviour = tinted + vignetted + faded to page-bg. Tyler @ Toka
  // 2026-09-23 wanted this OFF for imagery that stands on its own; false
  // renders the raw photo (still with grain) but no colour cast or fade.
  const dim = b.dimOverlay !== false;
  return (
    <section className={`relative flex flex-col overflow-hidden ${b.full || isHome ? "min-h-[92vh]" : "min-h-[48vh]"}`}>
      {b.imageUrl && (
        <img src={b.imageUrl} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter: dim ? "brightness(0.8) contrast(1.1) saturate(1.05)" : undefined }} />
      )}
      {/* Scrims: a full-bleed tint casts the whole photo in the venue colour
          (not just bright edges over a raw image), a radial vignette darkens
          the corners/edges, then top/bottom gradients deepen to fully solid
          so the section resolves into the page background with no seam. */}
      {dim && (
        <>
          <div className="absolute inset-0" style={{ background: "color-mix(in srgb, var(--venue-bg) 55%, transparent)" }} aria-hidden />
          <div className="absolute inset-0" style={{ background: "radial-gradient(120% 95% at 50% 42%, transparent 35%, color-mix(in srgb, var(--venue-bg) 70%, transparent) 100%)" }} aria-hidden />
          <div className="absolute inset-x-0 top-0 h-36" style={{ background: "linear-gradient(color-mix(in srgb, var(--venue-bg) 94%, transparent), transparent)" }} aria-hidden />
          <div className="absolute inset-x-0 bottom-0 h-3/5" style={{ background: "linear-gradient(transparent, color-mix(in srgb, var(--venue-bg) 75%, transparent) 45%, var(--venue-bg) 100%)" }} aria-hidden />
        </>
      )}
      {b.imageUrl && <GrainOverlay fadeBottom />}
      {isHome && <HeroAccents />}

      {nav}

      {/* Same composition as WRAP (mx-auto max-w-6xl px-6), plus an explicit
          w-full: this section is `flex flex-col`, and a flex child with
          mx-auto (auto cross-axis margins) but no explicit width shrinks to
          fit its own content instead of stretching to the container width
          before centering, for short content (e.g. the logo image) that
          silently centers a much-narrower-than-1152px box, drifting the
          whole thing right of where every non-flex WRAP'd section lines up.
          w-full gives it a definite size so it stretches-then-caps like a
          normal block, and mx-auto centers *that*, matching WRAP exactly. */}
      <div className={`relative z-10 mt-auto w-full mx-auto max-w-6xl px-6 ${isHome ? "pb-14 sm:pb-16" : "pb-4 sm:pb-5"} ${centered ? "flex flex-col items-center text-center" : ""}`}>
          {isHome ? (
            logoUrl ? (
              <img
                src={logoUrl}
                alt={name}
                className={`max-w-4xl object-contain ${centered ? "object-center" : "object-left"}`}
                style={{ height: "clamp(2.6rem, 9vw, 7rem)", width: "auto" }}
              />
            ) : (
              <h1
                className="max-w-4xl leading-[0.92]"
                style={{ ...WORDMARK_FONT, fontSize: "clamp(2.6rem, 9vw, 7rem)", color: "var(--accent)", textWrap: "balance" as never }}
              >
                {name}
              </h1>
            )
          ) : (
            b.heading && (
              b.headingStyle === "outline" ? (
                <h1
                  className="max-w-4xl uppercase leading-[0.95] tracking-tight"
                  style={{
                    // Poppins Black: bold geometric grotesk, tight kerning,
                    // circular counters, closer to the reference than the
                    // site's usual DISPLAY sans.
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    fontSize: "clamp(3.6rem, 11vw, 8.5rem)",
                    textWrap: "balance" as never,
                    // Fallback (solid) for browsers without text-stroke
                    // support, never render fully invisible text.
                    color: "var(--accent)",
                    WebkitTextStroke: "2px var(--accent)",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {b.heading}
                </h1>
              ) : (
                <h1
                  className="max-w-4xl font-extrabold uppercase leading-[0.95] tracking-tight"
                  style={{ ...DISPLAY, fontSize: "clamp(1.4rem, 3vw, 2.3rem)", textWrap: "balance" as never }}
                >
                  {b.heading}
                </h1>
              )
            )
          )}
          {b.sub && (
            <p className={`${isHome ? "mt-3" : "mt-2"} max-w-xl text-[15px] sm:text-base`} style={{ color: "color-mix(in srgb, var(--venue-text) 82%, transparent)" }}>
              {b.sub}
            </p>
          )}
          {(b.ctaLabel || b.cta2Label) && (
            <div className={`${isHome ? "mt-7" : "mt-4"} flex flex-wrap gap-3`}>
              {b.ctaLabel && b.ctaHref && (
                <a
                  href={b.ctaHref}
                  onClick={() => { const l = heroCtaLabel(b.ctaHref!); if (l) pushCtaClick(l); }}
                  className={`${ctaBase} hover:opacity-85`}
                  style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
                >
                  {b.ctaLabel}
                </a>
              )}
              {b.cta2Label && b.cta2Href && (
                <a
                  href={b.cta2Href}
                  onClick={() => { const l = heroCtaLabel(b.cta2Href!); if (l) pushCtaClick(l); }}
                  className={`${ctaBase} border hover:bg-white/10`}
                  style={{ borderColor: "color-mix(in srgb, var(--venue-text) 45%, transparent)" }}
                >
                  {b.cta2Label}
                </a>
              )}
            </div>
          )}
      </div>
    </section>
  );
}

function SectionHeading({ kicker, heading }: { kicker?: string; heading?: string }) {
  if (!kicker && !heading) return null;
  return (
    <div>
      {kicker && <p className={KICKER} style={{ color: "var(--accent)" }}>{kicker}</p>}
      {heading && (
        <h2 className="mt-1.5 font-normal uppercase tracking-tight" style={{ ...DISPLAY, fontSize: "clamp(1.05rem, 2vw, 1.5rem)" }}>
          {heading}
        </h2>
      )}
    </div>
  );
}

// Shared image+content two-column layout, page margins (WRAP), a gutter
// between columns, and the image capped to a controlled aspect ratio
// (rather than stretching to match the content column's height) so it
// reads as an accent next to the text, not a competing full-bleed block.
// `flip` puts the image on the right; alternate it block-to-block so a
// stack of these reads as a flowing page rather than a repeated template.
function ImageContentSplit({
  imageUrl,
  flip,
  children,
  paddingClassName = "py-14 sm:py-16",
  imageAspect = "4/3",
  contentFirstOnMobile = false,
}: {
  imageUrl: string;
  flip?: boolean;
  children: React.ReactNode;
  /** Override when a block needs less top/bottom breathing room than the default. */
  paddingClassName?: string;
  /** Shorter ratios (e.g. "16/10") keep the image from towering over a shorter text column. */
  imageAspect?: string;
  /** Default stacks image-then-content on mobile (single column) regardless
      of flip. Set true to keep content first on mobile too (e.g. contact
      info you want visible before scrolling past a photo). */
  contentFirstOnMobile?: boolean;
}) {
  const imageOrder = contentFirstOnMobile
    ? flip
      ? "order-2"
      : "order-2 md:order-1"
    : flip
      ? "md:order-2"
      : "";
  return (
    <section className={`${WRAP} ${paddingClassName}`}>
      <div className="grid items-center gap-8 md:grid-cols-2 md:gap-16">
        <div className={`relative overflow-hidden ${imageOrder}`}>
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "contrast(1.05) saturate(1.1)", aspectRatio: imageAspect }}
          />
          <GrainOverlay />
        </div>
        <div className={contentFirstOnMobile ? "order-1" : ""}>{children}</div>
      </div>
    </section>
  );
}

function Block({
  block,
  venueId,
  venueName,
  venueLogoUrl,
}: {
  block: SiteBlock;
  venueId: string;
  venueName: string;
  venueLogoUrl: string | null;
}) {
  switch (block.type) {
    case "hero":
      // A hero mid-page (not the lead) renders as a full-bleed banner.
      return (
        <section className="relative flex min-h-[46vh] items-end overflow-hidden">
          {block.imageUrl && (
            <>
              <img src={block.imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ filter: "contrast(1.06) saturate(1.12)" }} />
              <div className="absolute inset-0" style={{ background: "linear-gradient(transparent 30%, color-mix(in srgb, var(--venue-bg) 90%, transparent))" }} aria-hidden />
              <GrainOverlay fadeBottom />
            </>
          )}
          <div className={`${WRAP} relative w-full pb-10 pt-24`}>
            {block.heading && <h2 className="font-extrabold uppercase tracking-tight" style={{ ...DISPLAY, fontSize: "clamp(2rem, 5vw, 3.6rem)" }}>{block.heading}</h2>}
          </div>
        </section>
      );
    case "text":
      return (
        <section className={`${WRAP} py-16 sm:py-20`}>
          <SectionHeading heading={block.heading} />
          <div className={`${block.heading ? "mt-5" : ""} max-w-3xl space-y-4`}>
          {block.body.split(/\n\s*\n/).map((p, i) => (
            <p key={i} className="text-[16px] leading-relaxed sm:text-[17px]" style={{ color: MUTED }}>{p.trim()}</p>
          ))}
          </div>
        </section>
      );
    case "imageText":
      return (
        <ImageContentSplit imageUrl={block.imageUrl} flip={block.flip}>
          <SectionHeading heading={block.heading} />
          {block.body && (
            <div className="mt-4 space-y-3">
              {block.body.split(/\n\s*\n/).map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed sm:text-[16px]" style={{ color: MUTED }}>{p.trim()}</p>
              ))}
            </div>
          )}
          {block.buttonLabel && block.buttonHref && (
            <a
              href={block.buttonHref}
              className="mt-5 inline-flex h-11 items-center justify-center rounded-lg px-5 text-[11px] font-bold uppercase tracking-[0.26em] transition hover:opacity-85"
              style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
            >
              {block.buttonLabel}
            </a>
          )}
        </ImageContentSplit>
      );
    case "gallery":
      return <GallerySection block={block} />;
    case "events":
      return <EventsBlock heading={block.heading} limit={block.limit} venueId={venueId} />;
    case "eventsCarousel":
      return <EventsCarouselBlock heading={block.heading} limit={block.limit} items={block.items} venueId={venueId} venueName={venueName} venueLogoUrl={venueLogoUrl} />;
    case "agenda":
      return <AgendaBlock heading={block.heading} limit={block.limit} items={block.items} />;
    case "faq":
      return <FaqBlock heading={block.heading} items={block.items} imageUrl={block.imageUrl} flip={block.flip} variant={block.variant} />;
    case "hours": {
      const content = (
        <>
          <SectionHeading heading={block.heading} />
          <table className="mt-6 w-full max-w-md">
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i} style={{ borderTop: i ? `1px solid ${HAIR}` : undefined }}>
                  <td className="py-3 pr-6 text-[13px] font-bold uppercase tracking-[0.14em]">{r.label}</td>
                  <td className="py-3 text-[15px]" style={{ color: MUTED }}>{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {block.note && (
            <p className="mt-4 max-w-md text-[15px]" style={{ color: MUTED }}>{block.note}</p>
          )}
        </>
      );
      return block.imageUrl ? (
        <ImageContentSplit imageUrl={block.imageUrl} flip={block.flip}>{content}</ImageContentSplit>
      ) : (
        <section className={`${WRAP} py-16`}>{content}</section>
      );
    }
    case "contact": {
      const content = (
        <>
          <SectionHeading heading={block.heading} />
          {block.body && <p className="mt-4 max-w-2xl text-[16px] leading-relaxed" style={{ color: MUTED }}>{block.body}</p>}
          <div className="mt-7 space-y-2">
            {block.email && (
              <p>
                <a href={`mailto:${block.email}`} className="font-bold underline decoration-1 underline-offset-4 text-[15px] sm:text-[16px]" style={{ color: "var(--venue-text)", textDecorationColor: "var(--accent)" }}>
                  {block.email}
                </a>
              </p>
            )}
            {block.phone && <p className="text-[15px]" style={{ color: MUTED }}>{block.phone}</p>}
            {block.address && <p className="text-[13px] font-bold uppercase tracking-[0.18em]" style={{ color: MUTED }}>{block.address}</p>}
          </div>
        </>
      );
      return block.imageUrl ? (
        <ImageContentSplit imageUrl={block.imageUrl} flip={block.flip} contentFirstOnMobile>{content}</ImageContentSplit>
      ) : (
        <section className={`${WRAP} py-16 sm:py-20`}>{content}</section>
      );
    }
    case "audio":
      return (
        <section className={`${WRAP} py-16`}>
          <SectionHeading heading={block.heading} />
          {block.sub && <p className="mt-3 max-w-2xl text-[15px]" style={{ color: MUTED }}>{block.sub}</p>}
          <div className="mt-7 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {block.items.map((it, i) => (
              <a key={i} href={it.href} target="_blank" rel="noopener noreferrer" className="group p-5 transition hover:bg-white/[0.05]" style={{ border: `1px solid ${HAIR}` }}>
                {it.imageUrl && <img src={it.imageUrl} alt="" className="mb-4 aspect-square w-full object-cover" />}
                <p className="text-[15px] font-extrabold uppercase leading-snug tracking-tight">{it.title}</p>
                {it.meta && <p className="mt-1 text-[12px]" style={{ color: MUTED }}>{it.meta}</p>}
                <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.26em]" style={{ color: "var(--accent)" }}>Listen ↗</p>
              </a>
            ))}
          </div>
        </section>
      );
    case "tiles":
      return <TilesSection block={block} />;
    case "splash":
      return <SplashBlock block={block} venueId={venueId} />;
    case "subscribe":
      return <SubscribeBlock heading={block.heading} sub={block.sub} venueId={venueId} venueName={venueName} imageUrl={block.imageUrl} flip={block.flip} listId={block.listId} collectPhone={block.collectPhone} collectSmsConsent={block.collectSmsConsent} />;
    case "cta":
      return (
        <section className={`${WRAP} py-8`}>
          <a href={block.href} className="inline-block px-7 py-3 text-[11px] font-bold uppercase tracking-[0.26em] transition hover:opacity-85" style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}>
            {block.label}
          </a>
        </section>
      );
    case "appPromo": {
      const shots = block.screenshots.slice(0, 3);
      // Fanned cluster: the middle shot sits in front, larger and upright;
      // the outer two rotate slightly outward and drop behind, the classic
      // "app store hero" phone stack, not just 3 flat screenshots.
      const rotate = ["-8deg", "0deg", "8deg"];
      const lift = ["mt-6", "mt-0", "mt-6"];
      return (
        <section className={`${WRAP} py-16 sm:py-20`}>
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
            <div className="order-2 flex flex-col items-center justify-center md:order-1">
              <div className="flex max-w-[380px] items-start">
                {shots.map((src, i) => (
                  <div
                    key={i}
                    className={`w-[38%] shrink-0 ${lift[i] ?? ""}`}
                    style={{
                      marginLeft: i > 0 ? "-9%" : undefined,
                      transform: `rotate(${rotate[i] ?? "0deg"})`,
                      zIndex: i === 1 ? 2 : 1,
                    }}
                  >
                    <PhoneFrame src={src} />
                  </div>
                ))}
              </div>
              {/* Mobile only: same CTA, positioned right after the phone
                  cluster instead of down in the text column (which comes
                  first on mobile), the desktop instance below is unaffected. */}
              {block.ctaLabel && block.ctaHref && (
                <a
                  href={block.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => pushCtaClick("download")}
                  className="mt-8 inline-flex h-12 min-w-[176px] items-center justify-center rounded-lg px-6 text-[11px] font-bold uppercase tracking-[0.26em] transition hover:opacity-85 md:hidden"
                  style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
                >
                  {block.ctaLabel}
                </a>
              )}
            </div>
            <div className="order-1 md:order-2">
              <SectionHeading heading={block.heading} />
              {block.body && (
                <p className="mt-4 max-w-md text-[15px] leading-relaxed sm:text-[16px]" style={{ color: MUTED }}>
                  {block.body}
                </p>
              )}
              {block.ctaLabel && block.ctaHref && (
                <a
                  href={block.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => pushCtaClick("download")}
                  className="mt-7 hidden h-12 min-w-[176px] items-center justify-center rounded-lg px-6 text-[11px] font-bold uppercase tracking-[0.26em] transition hover:opacity-85 md:inline-flex"
                  style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
                >
                  {block.ctaLabel}
                </a>
              )}
            </div>
          </div>
        </section>
      );
    }
    case "columns": {
      // 2 / 3-column magazine layout. Cells stack on mobile (< sm), sit side
      // by side on desktop. Each cell is either an image (uploaded via the
      // media library) or a small text card. Toka 2026-09-19.
      const gridCls = block.cols === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2";
      return (
        <section className={`${WRAP} py-10 sm:py-14`}>
          {block.heading && (
            <h2 className="mb-6 text-2xl font-semibold tracking-tight text-[var(--foreground)] sm:text-3xl">
              {block.heading}
            </h2>
          )}
          <div className={`grid gap-6 ${gridCls}`}>
            {block.cells.map((cell, i) => {
              if (cell.kind === "image") {
                if (!cell.imageUrl) {
                  return (
                    <div
                      key={i}
                      className="flex aspect-[4/3] items-center justify-center rounded-[var(--radius)] bg-[var(--surface-2)] text-xs text-[var(--muted)]"
                    >
                      Image placeholder
                    </div>
                  );
                }
                return (
                  <figure key={i} className="m-0">
                    {cell.title && (
                      <h3 className="mb-2 text-lg font-semibold text-[var(--foreground)] sm:text-xl">{cell.title}</h3>
                    )}
                    <img
                      src={cell.imageUrl}
                      alt={cell.title ?? cell.caption ?? ""}
                      className="aspect-[4/3] w-full rounded-[var(--radius)] object-cover"
                    />
                    {cell.body && (
                      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[var(--foreground)]/85">
                        {cell.body}
                      </p>
                    )}
                    {cell.caption && !cell.body && (
                      <figcaption className="mt-2 text-xs text-[var(--muted)]">{cell.caption}</figcaption>
                    )}
                  </figure>
                );
              }
              const align = cell.align === "center" ? "text-center" : "text-left";
              return (
                <div key={i} className={`flex flex-col justify-center gap-2 ${align}`}>
                  {cell.heading && (
                    <h3 className="text-lg font-semibold text-[var(--foreground)] sm:text-xl">{cell.heading}</h3>
                  )}
                  {cell.body && (
                    <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--foreground)]/85 sm:text-base">
                      {cell.body}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      );
    }
    case "freeform": {
      // Free-form canvas: absolute-positioned items, %-based so the layout
      // scales with viewport width. On mobile (< sm) we bail on the absolute
      // layout entirely and stack items vertically in author order, because
      // arbitrary positions rarely translate to a portrait viewport.
      // In the ADMIN PREVIEW IFRAME (Toka 2026-09-19 Squarespace-style
      // editing) we render an INTERACTIVE variant: hover + drag + resize
      // handles overlaid on the items, sending commit events back to the
      // parent editor via postMessage. Outside the iframe it's the plain
      // renderer (public visitors get zero editor chrome).
      const items = block.items ?? [];
      if (items.length === 0) return null;
      return <FreeformSection block={block} items={items} />;
    }
    default:
      return null;
  }
}

// ── BlockAdminOverlay ─────────────────────────────────────────────────────
// When the site renders inside the ADMIN PREVIEW IFRAME, every top-level
// block gets wrapped in this: hover → dashed accent outline + floating chip
// with the block type name + toolbar (↑ ↓ Edit ✕). Toolbar clicks postMessage
// back to the parent editor which reorders/deletes the block or scrolls its
// property form into view. Public visitors get zero overlay (bypasses the
// wrapper entirely).
//
// The freeform-canvas block ALSO has per-item drag inside itself
// (FreeformItemEditOverlay); this outer wrapper is orthogonal and operates
// at the block level. Toka 2026-09-20.
function BlockAdminOverlay({
  block,
  blockIndex,
  totalBlocks,
  children,
}: {
  block: SiteBlock;
  blockIndex: number;
  totalBlocks: number;
  children: React.ReactNode;
}) {
  const inAdmin = useInAdminPreview();
  const [hover, setHover] = useState(false);
  const [toolbarHover, setToolbarHover] = useState(false);

  // Even when NOT in admin, we still provide the BlockIndexContext so any
  // descendants that read it get a value (they won't render admin-only
  // chrome because they also gate on useInAdminPreview themselves, but
  // provider-less useContext would return null which is fine anyway).
  if (!inAdmin) {
    return (
      <BlockIndexContext.Provider value={blockIndex}>
        {children}
      </BlockIndexContext.Provider>
    );
  }

  const post = (msg: PreviewReorderMessage | PreviewDeleteMessage | PreviewSelectMessage) => {
    if (typeof window === "undefined" || window.self === window.top) return;
    window.parent.postMessage(msg, window.location.origin);
  };
  const moveUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (blockIndex <= 0) return;
    post({ type: PREVIEW_REORDER_MSG_TYPE, blockIndex, delta: -1 });
  };
  const moveDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (blockIndex >= totalBlocks - 1) return;
    post({ type: PREVIEW_REORDER_MSG_TYPE, blockIndex, delta: 1 });
  };
  const del = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this ${BLOCK_LABELS[block.type]}? This can be undone by not saving.`)) return;
    post({ type: PREVIEW_DELETE_MSG_TYPE, blockIndex });
  };
  const edit = (e: React.MouseEvent) => {
    e.stopPropagation();
    post({ type: PREVIEW_SELECT_MSG_TYPE, blockIndex });
  };

  // Both hover-signals used to decide visibility: hovering the block OR the
  // floating toolbar (the toolbar can extend outside the block's bounding
  // box, moving the cursor onto it would otherwise fire pointerleave on the
  // block and hide the toolbar).
  const showChrome = hover || toolbarHover;

  return (
    <BlockIndexContext.Provider value={blockIndex}>
    <div
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onClick={edit}
      className="relative"
      style={{
        outline: showChrome ? "3px dashed var(--accent, #3f7a6a)" : "1px solid transparent",
        outlineOffset: showChrome ? "-3px" : "0",
        cursor: showChrome ? "pointer" : undefined,
        transition: "outline-color 120ms ease",
      }}
      title={showChrome ? `Click to edit this ${BLOCK_LABELS[block.type]}` : undefined}
    >
      {children}
      {/* Type chip (top-left) */}
      {showChrome && (
        <span
          className="pointer-events-none absolute z-40 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider shadow-lg"
          style={{
            top: "8px",
            left: "8px",
            backgroundColor: "var(--accent, #3f7a6a)",
            color: "var(--accent-fg, white)",
            letterSpacing: "0.08em",
          }}
        >
          {BLOCK_LABELS[block.type].split(" (")[0]}
        </span>
      )}
      {/* Toolbar (top-right) */}
      {showChrome && (
        <div
          onPointerEnter={() => setToolbarHover(true)}
          onPointerLeave={() => setToolbarHover(false)}
          className="absolute z-40 flex items-center gap-1 rounded-full px-1.5 py-1 shadow-lg"
          style={{
            top: "8px",
            right: "8px",
            backgroundColor: "var(--accent, #3f7a6a)",
            color: "var(--accent-fg, white)",
          }}
        >
          <ToolbarBtn onClick={moveUp} disabled={blockIndex <= 0} title="Move up">↑</ToolbarBtn>
          <ToolbarBtn onClick={moveDown} disabled={blockIndex >= totalBlocks - 1} title="Move down">↓</ToolbarBtn>
          <ToolbarBtn onClick={edit} title="Edit this block in the left panel">Edit</ToolbarBtn>
          <ToolbarBtn onClick={del} title="Delete this block" danger>✕</ToolbarBtn>
        </div>
      )}
    </div>
    </BlockIndexContext.Provider>
  );
}

function ToolbarBtn({
  onClick,
  disabled,
  title,
  danger,
  children,
}: {
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  title: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="flex h-6 min-w-[24px] items-center justify-center rounded-full border-0 bg-transparent px-2 text-[11px] font-bold text-inherit transition-colors hover:bg-black/20 disabled:opacity-30"
      style={{
        color: "inherit",
        backgroundColor: danger ? "rgba(0,0,0,0)" : undefined,
      }}
    >
      {children}
    </button>
  );
}

// ── GallerySection ────────────────────────────────────────────────────────
// Two variants:
//   - Public: the original masonry/grid layout. Zero interaction.
//   - Admin preview: same layout wrapped in @dnd-kit SortableContext so
//     the operator can drag images to reorder. On drop, postMessage
//     PREVIEW_GALLERY_REORDER back to the parent editor which moves the
//     URL inside the block's `images` array. The library handles the
//     mid-drag slide-under-past-midpoint animation via rectSortingStrategy.
//
// Note on masonry + drag: the public site uses CSS `columns`, which fights
// with @dnd-kit's transform-based reorder animation. In admin preview we
// render masonry as a CSS GRID with auto-flow: dense so the drag feels
// right; on save the operator sees the true masonry layout in the public
// site (they can toggle back to plain masonry in the editor's layout
// select). Toka 2026-09-20.
function GallerySection({ block }: { block: Extract<SiteBlock, { type: "gallery" }> }) {
  const inAdmin = useInAdminPreview();
  const blockIndex = useBlockIndex();
  if (inAdmin && blockIndex != null) {
    return <SortableGallerySection block={block} blockIndex={blockIndex} />;
  }
  // Public site: unchanged from the pre-2026-09-20 code path.
  if (block.layout === "grid") {
    const desktopCols = block.images.length <= 4 ? "md:grid-cols-2" : "md:grid-cols-3";
    return (
      <section className="px-1.5 py-10">
        <div className={`grid grid-cols-2 gap-1.5 ${desktopCols}`}>
          {block.images.map((src, i) => (
            <div key={i} className="relative overflow-hidden" style={{ aspectRatio: "1/1" }}>
              <img
                src={src}
                alt=""
                loading={i < 6 ? "eager" : "lazy"}
                className="h-full w-full object-cover transition duration-700 hover:scale-[1.04]"
                style={{ filter: "contrast(1.06) saturate(1.12)" }}
              />
              <GrainOverlay />
            </div>
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className="px-1.5 py-10">
      <div className="columns-2 gap-1.5 md:columns-3">
        {block.images.map((src, i) => (
          <div key={i} className="relative mb-1.5 overflow-hidden break-inside-avoid">
            <img
              src={src}
              alt=""
              loading={i < 6 ? "eager" : "lazy"}
              className="w-full transition duration-700 hover:scale-[1.04]"
              style={{ filter: "contrast(1.06) saturate(1.12)", aspectRatio: i % 3 === 1 ? "1/1" : i % 3 === 2 ? "3/4" : undefined, objectFit: "cover" }}
            />
            <GrainOverlay />
          </div>
        ))}
      </div>
    </section>
  );
}

function SortableGallerySection({
  block,
  blockIndex,
}: {
  block: Extract<SiteBlock, { type: "gallery" }>;
  blockIndex: number;
}) {
  const sensors = useSensors(
    // 6px activation distance so clicking a photo without wanting to drag
    // doesn't accidentally start one.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Sortable ids: URL + index so duplicate URLs (rare but possible) don't
  // collide inside SortableContext. We reconstitute the URL from the id
  // when posting the reorder message.
  const ids = block.images.map((src, i) => `${i}::${src}`);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    if (typeof window === "undefined" || window.self === window.top) return;
    const msg: PreviewGalleryReorderMessage = {
      type: PREVIEW_GALLERY_REORDER_MSG_TYPE,
      blockIndex,
      from,
      to,
    };
    window.parent.postMessage(msg, window.location.origin);
  };

  const useSquares = block.layout === "grid";

  return (
    <section className="px-1.5 py-10">
      <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)]">
        Drag any photo to reorder,  drop to save.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div
            className="grid gap-1.5"
            style={{
              // Admin preview always renders as a real CSS grid (works with
              // dnd-kit's rectSortingStrategy). Grid layout = uniform squares
              // via aspect-ratio on the child; masonry approximated with
              // auto-flow: dense + varied row spans so it still LOOKS mixed
              // during editing.
              gridTemplateColumns: useSquares
                ? (block.images.length <= 4 ? "repeat(2, 1fr)" : "repeat(3, 1fr)")
                : "repeat(3, 1fr)",
              gridAutoFlow: useSquares ? undefined : "dense",
            }}
          >
            {block.images.map((src, i) => (
              <SortableGalleryImage
                key={ids[i]}
                id={ids[i]}
                src={src}
                index={i}
                squares={useSquares}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}

function SortableGalleryImage({
  id,
  src,
  index,
  squares,
}: {
  id: string;
  src: string;
  index: number;
  squares: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const aspectRatio = squares
    ? "1/1"
    : index % 3 === 1
      ? "1/1"
      : index % 3 === 2
        ? "3/4"
        : "4/5";
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="relative cursor-grab overflow-hidden rounded-sm active:cursor-grabbing"
      style={{
        aspectRatio,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : "auto",
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging
          ? "0 20px 40px -18px rgba(0,0,0,0.55),  0 0 0 3px var(--accent, #3f7a6a)"
          : undefined,
        touchAction: "none",
      }}
      title="Drag to reorder"
    >
      <img
        src={src}
        alt=""
        className="pointer-events-none h-full w-full object-cover"
        style={{ filter: "contrast(1.06) saturate(1.12)" }}
        draggable={false}
      />
      <GrainOverlay />
    </div>
  );
}

// ── TilesSection ──────────────────────────────────────────────────────────
// "Pick a site" style clickable tiles (photo backdrop + title + sub + CTA).
// Same treatment as GallerySection: public path unchanged, admin preview
// wraps each tile in a sortable so operators can drag-reorder,  drop-to-save
// via the generic PREVIEW_ITEM_REORDER message. Tile anchors keep their
// hrefs but pointer-events go to the drag wrapper in admin mode so a drag
// doesn't accidentally navigate. Toka 2026-09-20.
type TileItem = Extract<SiteBlock, { type: "tiles" }>["items"][number];

function TilesSection({ block }: { block: Extract<SiteBlock, { type: "tiles" }> }) {
  const inAdmin = useInAdminPreview();
  const blockIndex = useBlockIndex();
  const cols = block.items.length === 2 ? "sm:grid-cols-2" : block.items.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3";

  if (inAdmin && blockIndex != null) {
    return <SortableTilesSection block={block} blockIndex={blockIndex} cols={cols} />;
  }

  // fullWidth = break out of the page's max-width container so tiles span
  // edge-to-edge, useful when tiles are meant to feel like a hero splash
  // rather than a card grid (Tyler @ Toka 2026-09-23).
  const sectionCls = block.fullWidth ? "w-full py-10" : "px-1.5 py-10";
  return (
    <section className={sectionCls}>
      {block.heading && (
        <div className={`${WRAP} pb-8`}>
          <SectionHeading heading={block.heading} />
        </div>
      )}
      <div className={`grid grid-cols-1 gap-1.5 ${cols}`}>
        {block.items.map((it, i) => <PublicTile key={i} item={it} index={i} textColor={block.textColor} />)}
      </div>
    </section>
  );
}

function PublicTile({ item, index, textColor }: { item: TileItem; index: number; textColor?: string }) {
  const external = /^https?:\/\//i.test(item.href);
  return (
    <a
      href={item.href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="group relative flex min-h-[46vh] items-center justify-center overflow-hidden p-8 text-center"
      style={{ border: `1px solid ${HAIR}`, color: textColor ?? undefined }}
    >
      {item.imageUrl && (
        <>
          <img
            src={item.imageUrl}
            alt=""
            loading={index < 3 ? "eager" : "lazy"}
            className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.05]"
            style={{ filter: "contrast(1.06) saturate(1.12) brightness(0.55)" }}
          />
          <GrainOverlay />
        </>
      )}
      <div className="relative">
        <p className="font-extrabold uppercase tracking-tight" style={{ ...DISPLAY, fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)", lineHeight: 1.05, color: textColor ?? undefined }}>{item.title}</p>
        {item.sub && <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.26em]" style={{ color: textColor ?? (item.imageUrl ? "color-mix(in srgb, var(--venue-text) 82%, transparent)" : MUTED) }}>{item.sub}</p>}
        <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.26em] opacity-0 transition group-hover:opacity-100" style={{ color: "var(--accent)" }}>
          {external ? "Visit ↗" : "Enter →"}
        </p>
      </div>
    </a>
  );
}

function SortableTilesSection({
  block,
  blockIndex,
  cols,
}: {
  block: Extract<SiteBlock, { type: "tiles" }>;
  blockIndex: number;
  cols: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = block.items.map((_, i) => `tile-${i}`);
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    if (typeof window === "undefined" || window.self === window.top) return;
    const msg: PreviewItemReorderMessage = {
      type: PREVIEW_ITEM_REORDER_MSG_TYPE,
      blockIndex,
      from,
      to,
    };
    window.parent.postMessage(msg, window.location.origin);
  };
  const sectionCls = block.fullWidth ? "w-full py-10" : "px-1.5 py-10";
  return (
    <section className={sectionCls}>
      {block.heading && (
        <div className={`${WRAP} pb-8`}>
          <SectionHeading heading={block.heading} />
        </div>
      )}
      <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)]">
        Drag any tile to reorder,  drop to save.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div className={`grid grid-cols-1 gap-1.5 ${cols}`}>
            {block.items.map((it, i) => <SortableTile key={ids[i]} id={ids[i]} item={it} index={i} textColor={block.textColor} />)}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}

function SortableTile({ id, item, index, textColor }: { id: string; item: TileItem; index: number; textColor?: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="relative flex min-h-[46vh] cursor-grab items-center justify-center overflow-hidden p-8 text-center active:cursor-grabbing"
      style={{
        border: `1px solid ${HAIR}`,
        color: textColor ?? undefined,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : "auto",
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging
          ? "0 20px 40px -18px rgba(0,0,0,0.55),  0 0 0 3px var(--accent, #3f7a6a)"
          : undefined,
        touchAction: "none",
      }}
      title="Drag to reorder"
    >
      {item.imageUrl && (
        <>
          <img
            src={item.imageUrl}
            alt=""
            loading={index < 3 ? "eager" : "lazy"}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={{ filter: "contrast(1.06) saturate(1.12) brightness(0.55)" }}
            draggable={false}
          />
          <GrainOverlay />
        </>
      )}
      <div className="pointer-events-none relative">
        <p className="font-extrabold uppercase tracking-tight" style={{ ...DISPLAY, fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)", lineHeight: 1.05, color: textColor ?? undefined }}>{item.title}</p>
        {item.sub && <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.26em]" style={{ color: textColor ?? (item.imageUrl ? "color-mix(in srgb, var(--venue-text) 82%, transparent)" : MUTED) }}>{item.sub}</p>}
      </div>
    </div>
  );
}

// ── FreeformSection ────────────────────────────────────────────────────────
// Renders the freeform canvas. Two modes:
//   - Public (window.top === window.self): plain absolute-positioned items on
//     desktop, stacked on mobile. Zero editor chrome.
//   - Admin preview iframe (window.top !== window.self): interactive. Hover
//     shows a "Click to move" pill; click selects; drag moves; corner handle
//     resizes. On drag/resize END, a `PREVIEW_EDIT_MSG_TYPE` postMessage
//     goes to the parent editor, which applies the patch to the block config
//     and pushes the new draft back into the iframe (via PREVIEW_MSG_TYPE)
//     for the next render.
//
// During a drag we apply a LOCAL CSS transform via ref so the item moves at
// 60fps without a message round-trip; on pointerup we commit and clear the
// transform (the parent's next render places it at the new x/y anyway).
// Toka 2026-09-19.
function FreeformSection({
  block,
  items,
}: {
  block: Extract<SiteBlock, { type: "freeform" }>;
  items: FreeformItem[];
}) {
  // Gated on our own admin preview only (framed + has received a same-origin
  // preview overlay message). Public visitors + third-party embeds get the
  // plain renderer with zero edit chrome.
  const framed = useInAdminPreview();

  return (
    <section className="mx-auto max-w-[1200px] px-6 py-10 sm:py-14">
      {/* Mobile stack: no positions, just body-only in author order. Skipped
          in the framed preview if the operator viewport is desktop, which
          is the default (admin preview iframe is laid out at ≥1280px). */}
      <div className="flex flex-col gap-6 sm:hidden">
        {items.map((it) => (
          <FreeformItemBody key={it.id} item={it} />
        ))}
      </div>
      {/* Desktop canvas: absolute-positioned items. */}
      <div
        className="relative hidden w-full overflow-hidden rounded-[var(--radius)] sm:block"
        style={{ height: block.height, backgroundColor: block.bg || undefined }}
      >
        {items.map((it) => (
          framed ? (
            <FreeformItemEditOverlay key={it.id} item={it} />
          ) : (
            <div
              key={it.id}
              className="absolute"
              style={{
                left: `${it.x}%`,
                top: `${it.y}%`,
                width: `${it.w}%`,
                height: `${it.h}%`,
                zIndex: it.z ?? 0,
                transform: it.rotation ? `rotate(${it.rotation}deg)` : undefined,
                transformOrigin: "center center",
              }}
            >
              <FreeformItemBody item={it} />
            </div>
          )
        ))}
      </div>
    </section>
  );
}

// One item inside the admin preview iframe. Wraps the plain body with hover
// affordance + drag/resize handlers that emit commit-on-pointerup postMessages
// back to the parent editor.
function FreeformItemEditOverlay({ item }: { item: FreeformItem }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState(false);
  const [selected, setSelected] = useState(false);
  // Live drag state, mirrored on the ref so the pointermove handler doesn't
  // trip the render loop for every frame. `mode` = null when no active drag.
  const dragRef = useRef<null | {
    mode: "move" | "resize";
    startX: number; startY: number;
    origItem: FreeformItem;
    parentW: number; parentH: number;
    pointerId: number;
    capturedEl: Element | null;
  }>(null);

  // Cleanup on unmount: release any lingering pointer capture and clear the
  // drag ref, so switching pages / closing the editor mid-drag doesn't leave
  // pointer state stuck. Peer review 2026-09-19 MED.
  useEffect(() => {
    return () => {
      const d = dragRef.current;
      if (d?.capturedEl) {
        try { d.capturedEl.releasePointerCapture?.(d.pointerId); } catch { /* ignore */ }
      }
      dragRef.current = null;
    };
  }, []);

  const commit = (patch: PreviewEditMessage["patch"]) => {
    // Send to the parent editor. The parent applies it to the block config
    // and pushes the updated draft back via PREVIEW_MSG_TYPE, at which point
    // this component will remount with the new x/y from props and we can
    // safely clear the local transform.
    if (typeof window === "undefined" || window.self === window.top) return;
    const msg: PreviewEditMessage = {
      type: PREVIEW_EDIT_MSG_TYPE,
      itemId: item.id,
      patch,
      commit: true,
    };
    window.parent.postMessage(msg, window.location.origin);
  };

  const applyLocal = (patch: { x?: number; y?: number; w?: number; h?: number }) => {
    // Live visual: set the element's style directly so the drag is 60fps
    // without a state round-trip. On commit + parent re-render these styles
    // are replaced by the item's new left/top/width/height from props.
    const el = wrapRef.current;
    if (!el) return;
    if (patch.x != null) el.style.left = `${patch.x}%`;
    if (patch.y != null) el.style.top = `${patch.y}%`;
    if (patch.w != null) el.style.width = `${patch.w}%`;
    if (patch.h != null) el.style.height = `${patch.h}%`;
  };

  const beginDrag = (e: React.PointerEvent, mode: "move" | "resize") => {
    e.preventDefault();
    e.stopPropagation();
    const parent = wrapRef.current?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    // First-frame guard: if the parent hasn't laid out yet (font load, brief
    // layout thrash) the rect is 0-sized and the percentage math is
    // meaningless. Bail rather than start a drag with garbage numbers.
    // Peer review 2026-09-19 MED.
    if (rect.width === 0 || rect.height === 0) return;
    const target = e.target as Element;
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origItem: item,
      parentW: rect.width,
      parentH: rect.height,
      pointerId: e.pointerId,
      capturedEl: target,
    };
    setSelected(true);
    target.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dxPct = ((e.clientX - d.startX) / d.parentW) * 100;
    const dyPct = ((e.clientY - d.startY) / d.parentH) * 100;
    if (d.mode === "move") {
      const x = Math.max(0, Math.min(100 - d.origItem.w, d.origItem.x + dxPct));
      const y = Math.max(0, Math.min(100 - d.origItem.h, d.origItem.y + dyPct));
      applyLocal({ x, y });
    } else {
      const w = Math.max(5, Math.min(100 - d.origItem.x, d.origItem.w + dxPct));
      const h = Math.max(5, Math.min(100 - d.origItem.y, d.origItem.h + dyPct));
      applyLocal({ w, h });
    }
  };

  const endDrag = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    // Release pointer capture BEFORE clearing ref, so a subsequent drag on
    // ANY other item's pointer target is not blocked by a lingering
    // capture. Peer review 2026-09-19 HIGH: without this, drag one item →
    // release → try to drag another → second item's handlers silently
    // don't fire because the first's captured element still holds the
    // pointer stream.
    if (d.capturedEl) {
      try { d.capturedEl.releasePointerCapture?.(d.pointerId); } catch { /* ignore */ }
    }
    const dxPct = ((e.clientX - d.startX) / d.parentW) * 100;
    const dyPct = ((e.clientY - d.startY) / d.parentH) * 100;
    let patch: PreviewEditMessage["patch"] = {};
    if (d.mode === "move") {
      const x = Math.max(0, Math.min(100 - d.origItem.w, d.origItem.x + dxPct));
      const y = Math.max(0, Math.min(100 - d.origItem.h, d.origItem.y + dyPct));
      // Snap to 5% grid so drops feel deliberate. Hold Shift for 0.1%
      // precision. Same rule as the left-panel canvas (freeform-canvas.ts
      // `snapPct`) so the two edit surfaces feel identical.
      if (!e.shiftKey) {
        patch = { x: Math.round(x / 5) * 5, y: Math.round(y / 5) * 5 };
      } else {
        patch = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
      }
    } else {
      const w = Math.max(5, Math.min(100 - d.origItem.x, d.origItem.w + dxPct));
      const h = Math.max(5, Math.min(100 - d.origItem.y, d.origItem.h + dyPct));
      if (!e.shiftKey) {
        patch = { w: Math.round(w / 5) * 5, h: Math.round(h / 5) * 5 };
      } else {
        patch = { w: Math.round(w * 10) / 10, h: Math.round(h * 10) / 10 };
      }
    }
    dragRef.current = null;
    commit(patch);
  };

  // Keyboard nudge for the selected item. Only fires when THIS item is
  // selected AND the wrap element has focus, so typing in an admin form
  // outside the iframe doesn't hijack. Arrow = 1%, Shift+arrow = 5%.
  useEffect(() => {
    if (!selected) return;
    const el = wrapRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      if (!el.contains(document.activeElement) && document.activeElement !== el) return;
      if (!e.key.startsWith("Arrow")) return;
      const step = e.shiftKey ? 5 : 1;
      e.preventDefault();
      let dx = 0, dy = 0;
      if (e.key === "ArrowLeft")  dx = -step;
      if (e.key === "ArrowRight") dx = step;
      if (e.key === "ArrowUp")    dy = -step;
      if (e.key === "ArrowDown")  dy = step;
      const x = Math.max(0, Math.min(100 - item.w, item.x + dx));
      const y = Math.max(0, Math.min(100 - item.h, item.y + dy));
      commit({ x, y });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, item.x, item.y, item.w, item.h]);

  // Kind label for the pill so operators know what they're about to grab
  // ("Text" vs "Image") — matches how the left-panel canvas shows the
  // selection kind in the property form ("Selected: text"/"image").
  const kindLabel = item.kind === "image" ? "Image" : "Text";

  return (
    <div
      ref={wrapRef}
      tabIndex={0}
      onPointerDown={(e) => beginDrag(e, "move")}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setSelected(true)}
      onBlur={(e) => {
        // Only deselect when focus leaves the item AND its descendants
        // (the resize handle is a child, moving to it should stay selected).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSelected(false);
      }}
      className="absolute select-none outline-none transition-shadow duration-150"
      style={{
        left: `${item.x}%`,
        top: `${item.y}%`,
        width: `${item.w}%`,
        height: `${item.h}%`,
        zIndex: item.z ?? 0,
        transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
        transformOrigin: "center center",
        cursor: selected ? "move" : "pointer",
        // Louder hover: 3px dashed accent for hover, 3px solid for selected.
        // Doubled ring size (5px) and 25%/35% opacity so the item pops against
        // whatever the venue background is (Toka's sauna palette is dark,
        // Love Inn's is warm cream, so pure white/black rings would clash).
        outline: selected
          ? "3px solid var(--accent, #3f7a6a)"
          : hover
            ? "3px dashed var(--accent, #3f7a6a)"
            : "1px solid transparent",
        outlineOffset: selected || hover ? "2px" : "0",
        boxShadow: selected
          ? "0 0 0 5px color-mix(in srgb, var(--accent, #3f7a6a) 35%, transparent),  0 12px 28px -12px color-mix(in srgb, var(--accent, #3f7a6a) 60%, transparent)"
          : hover
            ? "0 0 0 5px color-mix(in srgb, var(--accent, #3f7a6a) 25%, transparent),  0 10px 22px -14px color-mix(in srgb, var(--accent, #3f7a6a) 50%, transparent)"
            : "none",
        touchAction: "none",
      }}
    >
      <FreeformItemBody item={item} />
      {/* Chip label. On hover: "⇔ Click to move · Text". On select:
          "Text · N% × N%". Styled to match the admin's section-heading chip
          convention (uppercase, tracking-wider, tiny, bold) so operators
          recognise it as an admin affordance not part of the site content. */}
      {(hover || selected) && (
        <span
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider shadow-lg"
          style={{
            top: "-30px",
            backgroundColor: "var(--accent, #3f7a6a)",
            color: "var(--accent-fg, white)",
            letterSpacing: "0.08em",
          }}
        >
          {selected
            ? `${kindLabel} · ${Math.round(item.w)}% × ${Math.round(item.h)}%`
            : `✥  Click to move · ${kindLabel}`}
        </span>
      )}
      {/* Corner chevron on hover so the drag affordance is legible even
          when the pill is off-screen (item near the top of the canvas). */}
      {hover && !selected && (
        <span
          className="pointer-events-none absolute rounded-full shadow-md"
          style={{
            top: "-6px",
            left: "-6px",
            width: "14px",
            height: "14px",
            backgroundColor: "var(--accent, #3f7a6a)",
            border: "2px solid var(--accent-fg, white)",
          }}
          aria-hidden
        />
      )}
      {selected && (
        <div
          onPointerDown={(e) => beginDrag(e, "resize")}
          className="absolute h-6 w-6 cursor-se-resize rounded-md border-2 bg-white shadow-lg transition-transform hover:scale-110"
          style={{
            bottom: "-10px",
            right: "-10px",
            borderColor: "var(--accent, #3f7a6a)",
          }}
          title="Resize (Shift = free size)"
        />
      )}
    </div>
  );
}

// Renders a single freeform item's content, WITHOUT any positioning wrapper.
// Used twice by the freeform block: absolute-positioned on desktop, stacked
// vertically on mobile.
function FreeformItemBody({ item }: { item: FreeformItem }) {
  if (item.kind === "image") {
    if (!item.imageUrl) {
      return (
        <div className="flex h-full w-full items-center justify-center rounded-[var(--radius)] bg-[var(--surface-2)] text-xs text-[var(--muted)]">
          Image placeholder
        </div>
      );
    }
    // Clamp defensively — the editor slider caps at 3, but a hand-edited
    // JSON export (or a future migration bug) could push a 999 into the
    // DOM which would break the entire canvas layout (peer review
    // 2026-09-26). Same reasoning on the position axes.
    const rawScale = typeof item.imageScale === "number" ? item.imageScale : 1;
    const scale = rawScale > 1 ? Math.min(3, Math.max(1, rawScale)) : null;
    const clamp = (n: number) => Math.min(100, Math.max(0, n));
    const posX = typeof item.objectPositionX === "number" ? clamp(item.objectPositionX) : 50;
    const posY = typeof item.objectPositionY === "number" ? clamp(item.objectPositionY) : 50;
    return (
      <img
        src={item.imageUrl}
        alt={item.alt ?? ""}
        className="h-full w-full rounded-[var(--radius)]"
        style={{
          objectFit: item.fit === "contain" ? "contain" : "cover",
          // object-position only affects a cover/contain-fitted image.
          objectPosition: item.fit === "contain" ? undefined : `${posX}% ${posY}%`,
          // Scale zoom is applied via transform; scale > 1 combined with
          // overflow-hidden on the container gives the crop-inside-frame
          // behaviour Tyler asked for. Origin follows objectPosition so the
          // zoom pivots around the same point the author panned to.
          ...(scale
            ? { transform: `scale(${scale})`, transformOrigin: `${posX}% ${posY}%` }
            : {}),
        }}
      />
    );
  }
  // text card
  const sizeCls =
    item.size === "xl" ? "text-2xl sm:text-3xl"
    : item.size === "lg" ? "text-lg sm:text-xl"
    : item.size === "sm" ? "text-xs sm:text-sm"
    : "text-sm sm:text-base";
  const alignCls =
    item.align === "center" ? "text-center"
    : item.align === "right" ? "text-right"
    : "text-left";
  return (
    <div
      className={`flex h-full w-full flex-col justify-center whitespace-pre-line rounded-[var(--radius)] p-3 leading-relaxed text-[var(--foreground)] ${sizeCls} ${alignCls}`}
      style={{ backgroundColor: item.bg || undefined }}
    >
      {item.body}
    </div>
  );
}

// A CSS-only iPhone bezel (rounded frame + dynamic-island notch) around an
// app screenshot, used by the "appPromo" block's phone-mockup cluster.
function PhoneFrame({ src }: { src: string }) {
  return (
    <div
      className="relative overflow-hidden rounded-[1.6rem] shadow-2xl"
      style={{ aspectRatio: "1206 / 2622", border: "5px solid #0a0a0a", backgroundColor: "#0a0a0a" }}
    >
      <div className="absolute left-1/2 top-[3%] z-10 h-[4%] w-[34%] -translate-x-1/2 rounded-full bg-black" aria-hidden />
      <img src={src} alt="" className="h-full w-full object-cover" />
    </div>
  );
}

function EventsBlock({ heading, limit, venueId }: { heading?: string; limit?: number; venueId: string }) {
  const q = useQuery({
    // Distinct queryKey per response shape (React Query cache collision
    // otherwise white-screens users navigating /whats-on → /events; the
    // bare-array getVenueEvents caller uses ["events", venueId], we take
    // the wrapped {events, closingTimes} key). See PR #191 for the
    // venue.tsx counterpart + memory: project_react_query_conventions.md.
    queryKey: ["events-with-closing", venueId],
    queryFn: () => getVenueEventsWithClosing(venueId),
    staleTime: 60_000,
  });
  const rawEvents = q.data?.events ?? [];
  const closingTimes = q.data?.closingTimes;
  const liveEvent = rawEvents.find((e) => isEventLive(e, closingTimes));
  const events = (liveEvent ? rawEvents.filter((e) => e.id !== liveEvent.id) : rawEvents).slice(
    0,
    limit ?? 12,
  );
  return (
    <section className={`${WRAP} py-16 sm:py-20`}>
      {liveEvent && (
        <div className="mb-8">
          <LiveEventStrip event={liveEvent} closingTimes={closingTimes} variant="web" />
        </div>
      )}
      <SectionHeading heading={heading} />
      {events.length === 0 ? (
        <p className="mt-5 text-[16px]" style={{ color: MUTED }}>Nothing announced right now, check back soon.</p>
      ) : (
        <ul className={heading ? "mt-7" : ""}>
          {events.map((ev) => {
            const d = new Date(ev.date);
            const row = (
              <>
                <span className="flex shrink-0 flex-col leading-tight sm:flex-row sm:gap-0 sm:leading-normal whitespace-nowrap text-[12px] font-bold tracking-[0.01em] tabular-nums" style={{ color: "var(--accent)" }}>
                  <span>{d.toLocaleDateString("en-GB", { weekday: "short" }).toLowerCase()}<span className="hidden sm:inline">.</span></span>
                  <span>{String(d.getDate()).padStart(2, "0")}.{String(d.getMonth() + 1).padStart(2, "0")}</span>
                </span>
                <span className="min-w-0 flex-1 truncate text-[15px] sm:text-[16px]" style={{ color: "color-mix(in srgb, var(--venue-text) 90%, transparent)" }}>{ev.title}</span>
                <span className="hidden shrink-0 rounded-[4px] border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.22em] sm:inline-block" style={{ borderColor: "color-mix(in srgb, var(--venue-text) 40%, transparent)" }}>
                  Tickets{ev.externalTicketsUrl ? " ↗" : ""}
                </span>
                <span className="shrink-0 text-lg leading-none sm:hidden" style={{ color: "var(--accent)" }} aria-hidden>
                  {ev.externalTicketsUrl ? "↗" : "→"}
                </span>
              </>
            );
            const cls = "flex flex-wrap items-center sm:items-baseline gap-x-3 gap-y-1 py-5 transition hover:opacity-70";
            return (
              <li key={ev.id} style={{ borderBottom: `1px solid ${HAIR}` }}>
                {ev.externalTicketsUrl ? (
                  <a href={ev.externalTicketsUrl} target="_blank" rel="noopener noreferrer" className={cls}>{row}</a>
                ) : (
                  <Link to="/events/$id" params={{ id: ev.id }} className={cls}>{row}</Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// The app's horizontal "Upcoming Events" carousel (see pages/venue.tsx),
// ported to the editorial site look, sharp square cards instead of the
// app's rounded surfaces, DISPLAY/accent tokens instead of app CSS vars.
function EventsCarouselBlock({
  heading,
  limit,
  items,
  venueId,
  venueName,
  venueLogoUrl,
}: {
  heading?: string;
  limit?: number;
  items?: Array<{ date: string; title: string; sub?: string; imageUrl?: string; href: string }>;
  venueId: string;
  venueName: string;
  venueLogoUrl: string | null;
}) {
  // Editorial + cron merge. Static `items` (hand-curated with `sub` copy)
  // combine with the Assembly-fetched feed (populated by the daily
  // Headfirst cron). Static wins editorial fields; fetched fills gaps.
  // Prevents the class of bug that hid Love Inn's Last Bus Home gig on
  // 2026-09-26 — the static list was stale but the cron had already put
  // the event in the DB, and the old code took static-only when items
  // were present. See tests/unit/carousel-merge.test.ts + memory:
  // (Fenton audit, 2026-09-26).
  // Wave 0.2: pull venue tz + locale so carousel row dates render in
  // venue-local (not device-local). Same venue query the outer layout
  // uses; cached by TanStack Query so this is essentially free.
  const { data: venueData } = useVenue();
  const venue = venueData?.venue;
  const q = useQuery({
    // Distinct queryKey per response shape (React Query cache collision
    // otherwise white-screens users navigating /whats-on → /events; the
    // bare-array getVenueEvents caller uses ["events", venueId], we take
    // the wrapped {events, closingTimes} key). See PR #191 for the
    // venue.tsx counterpart + memory: project_react_query_conventions.md.
    queryKey: ["events-with-closing", venueId],
    queryFn: () => getVenueEventsWithClosing(venueId),
    staleTime: 60_000,
  });
  const fetchedEvents = q.data?.events ?? [];
  const closingTimes = q.data?.closingTimes;
  const liveEvent = fetchedEvents.find((e) => isEventLive(e, closingTimes));
  const merged = mergeCarouselItems({
    staticItems: items,
    fetchedEvents,
    limit: limit ?? 12,
  });
  const events = liveEvent
    ? merged.filter((e) => e.id !== liveEvent.id)
    : merged;
  return (
    <section className={`${WRAP} py-16 sm:py-20`}>
      {liveEvent && (
        <div className="mb-8">
          <LiveEventStrip
            event={liveEvent}
            closingTimes={closingTimes}
            variant="web"
            venueLogoUrl={venueLogoUrl}
            venueName={venueName}
          />
        </div>
      )}
      <SectionHeading heading={heading} />
      {events.length === 0 ? (
        <p className="mt-5 text-[16px]" style={{ color: MUTED }}>Nothing announced right now, check back soon.</p>
      ) : (
        <div className={`${heading ? "mt-7" : ""} -mx-6 px-6`}>
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory scroll-smooth">
            {events.map((ev) => {
              const card = (
                <>
                  <div className="relative aspect-square w-full overflow-hidden" style={{ border: `1px solid ${HAIR}` }}>
                    <PosterImage src={ev.imageUrl ?? undefined} alt="" venueLogoUrl={venueLogoUrl} venueName={venueName} />
                  </div>
                  <p className="mt-3 text-[12px] font-normal uppercase tracking-[0.16em] tabular-nums" style={{ color: "var(--accent)" }}>
                    {formatEventShortDate(ev.date, venue)}
                  </p>
                  <p className="mt-1 truncate text-[13px] font-normal uppercase leading-snug tracking-tight">{ev.title}</p>
                </>
              );
              const cls = "block w-44 shrink-0 snap-start transition hover:opacity-80 sm:w-52";
              return (
                <div key={ev.id}>
                  {ev.externalTicketsUrl ? (
                    <a href={ev.externalTicketsUrl} target="_blank" rel="noopener noreferrer" className={cls}>{card}</a>
                  ) : (
                    <Link to="/events/$id" params={{ id: ev.id }} className={cls}>{card}</Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// Static agenda, grouped by month (Thuishaven-style scroll), each row keeps
// date/title/ticket-button on one fixed grid so everything lines up
// regardless of label length; a "+" reveals the poster + blurb inline.
function AgendaBlock({
  heading,
  limit,
  items,
}: {
  heading?: string;
  limit?: number;
  items: Array<{ date: string; title: string; sub?: string; imageUrl?: string; href: string }>;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const shown = items.slice(0, limit ?? items.length);
  const groups: Array<[string, typeof shown]> = [];
  for (const it of shown) {
    const month = new Date(it.date).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const g = groups.find(([m]) => m === month);
    if (g) g[1].push(it);
    else groups.push([month, [it]]);
  }
  const dateCol = "w-16 shrink-0 text-[11px] font-bold uppercase tracking-[0.14em] tabular-nums sm:w-20 sm:text-[12px]";
  return (
    <section className={`${WRAP} py-16 sm:py-20`}>
      <SectionHeading heading={heading} />
      {shown.length === 0 ? (
        <p className="mt-5 text-[16px]" style={{ color: MUTED }}>Nothing announced right now, check back soon.</p>
      ) : (
        <div className={heading ? "mt-8 space-y-9" : "space-y-9"}>
          {groups.map(([month, rows]) => (
            <div key={month}>
              <p className={KICKER} style={{ color: "var(--accent)" }}>{month}</p>
              <div className="mt-3" style={{ borderTop: `1px solid ${HAIR}` }}>
                {rows.map((it) => {
                  const idx = shown.indexOf(it);
                  const isOpen = open === idx;
                  const d = new Date(it.date);
                  return (
                    <div key={idx} style={{ borderBottom: `1px solid ${HAIR}` }}>
                      <div className="grid grid-cols-[1fr_auto] items-center gap-3 sm:gap-5">
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : idx)}
                          aria-expanded={isOpen}
                          className="grid min-w-0 grid-cols-[4rem_1fr] items-center gap-3 border-0 bg-transparent p-0 py-5 text-left transition hover:opacity-70 sm:grid-cols-[5rem_1fr] sm:gap-5"
                        >
                          <span className={dateCol} style={{ color: "var(--accent)" }}>
                            {d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}
                          </span>
                          <span className="flex min-w-0 items-baseline gap-2">
                            <span className="min-w-0 truncate text-base font-extrabold uppercase tracking-tight sm:text-xl" style={DISPLAY}>
                              {it.title}
                            </span>
                            <span className="shrink-0 text-base leading-none" style={{ color: MUTED }} aria-hidden>{isOpen ? "–" : "+"}</span>
                          </span>
                        </button>
                        <a
                          href={it.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 border px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] transition hover:opacity-70 sm:px-5 sm:py-2"
                          style={{ borderColor: "color-mix(in srgb, var(--venue-text) 45%, transparent)" }}
                        >
                          Tickets ↗
                        </a>
                      </div>
                      {isOpen && (it.sub || it.imageUrl) && (
                        <div className="grid grid-cols-1 gap-4 pb-6 pl-0 sm:grid-cols-[140px_1fr] sm:pl-[6.25rem]">
                          {it.imageUrl && (
                            <img
                              src={it.imageUrl}
                              alt=""
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                              className="h-32 w-full object-cover sm:h-24 sm:w-36"
                              style={{ filter: "contrast(1.06) saturate(1.12)" }}
                            />
                          )}
                          {it.sub && (
                            <p className="text-[14px] leading-relaxed sm:text-[15px]" style={{ color: MUTED }}>{it.sub}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FaqBlock({
  heading,
  items,
  imageUrl,
  flip,
  variant,
}: {
  heading?: string;
  items: Array<{ q: string; a: string }>;
  imageUrl?: string;
  flip?: boolean;
  variant?: "default" | "sidebar";
}) {
  const [open, setOpen] = useState<number | null>(null);
  const questionList = (
    <div className="w-full">
      {items.map((it, i) => (
        <div key={i} style={{ borderTop: `1px solid ${HAIR}` }}>
          <button
            type="button"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
            className="flex w-full items-center justify-between gap-4 border-0 bg-transparent px-2 py-4 text-left text-[15px] font-extrabold uppercase tracking-tight transition hover:!translate-y-0 hover:!bg-transparent hover:!shadow-[0_2px_0_0_var(--accent)]"
            style={{ color: "var(--venue-text)" }}
          >
            {it.q}
            <span className="text-xl leading-none" style={{ color: "var(--accent)" }}>{open === i ? "–" : "+"}</span>
          </button>
          {open === i && <p className="px-2 pb-5 text-[15px] leading-relaxed" style={{ color: MUTED }}>{it.a}</p>}
        </div>
      ))}
      {/* Bottom rule so the last row visually closes the stack, matches the
          Oki Sauna reference Tyler shared. */}
      {items.length > 0 && <div style={{ borderTop: `1px solid ${HAIR}` }} />}
    </div>
  );

  // Sidebar variant (Tyler @ Toka 2026-09-23): heading floats left as a
  // section label, questions column sits on the right. Skipped when an image
  // is present — ImageContentSplit already owns the two-column layout.
  // The resolveable predicate lives in lib/site-pages.ts so we can unit-test
  // it in isolation.
  const layout = faqLayoutFor(variant, !!imageUrl);
  const isSidebar = layout === "sidebar";

  const content = isSidebar ? (
    <div className="grid grid-cols-1 gap-10 md:grid-cols-[minmax(160px,240px)_1fr] md:gap-16">
      {heading && (
        <div>
          <h2
            className="font-extrabold uppercase leading-tight tracking-tight"
            style={{ ...DISPLAY, fontSize: "clamp(1.4rem, 2.4vw, 2rem)", color: "var(--venue-text)" }}
          >
            {heading}
          </h2>
        </div>
      )}
      {questionList}
    </div>
  ) : (
    <>
      <SectionHeading heading={heading} />
      <div className="mt-6 max-w-3xl">{questionList}</div>
    </>
  );

  return imageUrl ? (
    <ImageContentSplit imageUrl={imageUrl} flip={flip}>{content}</ImageContentSplit>
  ) : (
    <section className={`${WRAP} py-16`}>{content}</section>
  );
}

function SplashBlock({
  block,
  venueId,
}: {
  block: Extract<SiteBlock, { type: "splash" }>;
  venueId: string;
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === "sending") return;
    pushCtaClick("subscribe");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setState("error");
      return;
    }
    setState("sending");
    try {
      await joinMailingList(venueId, email.trim(), "splash", undefined, block.listId);
      pushFormInteraction("success");
      setState("done");
      setEmail("");
    } catch {
      pushFormInteraction("fail");
      setState("error");
    }
  };

  const bg = block.backgroundColor || "#b8000e";
  const fg = block.foregroundColor || "#4a0004";
  const bodyFont = block.bodyFont || "Space Mono";
  const headingFont = block.headingFont || "Archivo Black";
  // Custom hosted fonts: inject an @font-face keyed to the family name so a
  // fontFamily reference below picks it up. No-op when no URL is set.
  const fontFaceCss = [
    block.headingFontUrl ? `@font-face{font-family:"${headingFont}";src:url("${block.headingFontUrl}");font-display:swap;}` : "",
    block.bodyFontUrl ? `@font-face{font-family:"${bodyFont}";src:url("${block.bodyFontUrl}");font-display:swap;}` : "",
  ].filter(Boolean).join("");
  return (
    <section
      className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-16 text-center"
      style={{ backgroundColor: bg, color: fg, fontFamily: `"${bodyFont}", ui-monospace, monospace` }}
    >
      {fontFaceCss && <style>{fontFaceCss}</style>}
      {block.kicker && (
        <p className="text-[14px] tracking-[0.02em] sm:text-[15px]" style={{ opacity: 0.9 }}>{block.kicker}</p>
      )}
      <h1
        className="mt-10 leading-[0.92]"
        style={{
          fontFamily: `"${headingFont}", ui-sans-serif, system-ui, sans-serif`,
          fontWeight: 900,
          letterSpacing: "-0.02em",
          fontSize: "clamp(3.5rem, 14vw, 8rem)",
          textTransform: "uppercase",
        }}
      >
        {block.heading}
      </h1>
      {block.sub && (
        <p className="mt-4 text-[14px] tracking-[0.02em] sm:text-[15px]" style={{ opacity: 0.9 }}>{block.sub}</p>
      )}
      {block.tagline && (
        <p className="mt-24 text-[14px] tracking-[0.02em] sm:text-[15px]" style={{ opacity: 0.9 }}>{block.tagline}</p>
      )}
      {block.showMailingList !== false && (
        <div className="mt-8 w-full max-w-sm">
          {state === "done" ? (
            <p className="text-[13px] tracking-[0.02em]" style={{ opacity: 0.9 }}>you're on the list.</p>
          ) : (
            <form onSubmit={submit} noValidate className="flex overflow-hidden" style={{ border: `1px solid ${fg}` }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={block.mailingListPlaceholder || "your email"}
                aria-label="Email address"
                className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-[13px] outline-none placeholder:opacity-50"
                style={{ color: fg, caretColor: fg }}
              />
              <button
                type="submit"
                disabled={state === "sending"}
                aria-label="Sign up"
                className="flex items-center border-0 px-4 text-[13px] font-bold uppercase tracking-[0.14em] transition hover:opacity-80 disabled:opacity-50"
                style={{ backgroundColor: fg, color: bg }}
              >
                {state === "sending" ? "…" : "sign up"}
              </button>
            </form>
          )}
          {state === "error" && <p className="mt-2 text-[12px]" style={{ opacity: 0.9 }}>Couldn't sign you up, try again.</p>}
        </div>
      )}
    </section>
  );
}

function SubscribeBlock({
  heading,
  sub,
  venueId,
  venueName,
  imageUrl,
  flip,
  listId,
  collectPhone,
  collectSmsConsent,
}: {
  heading?: string;
  sub?: string;
  venueId: string;
  venueName: string;
  imageUrl?: string;
  flip?: boolean;
  listId?: string;
  collectPhone?: boolean;
  collectSmsConsent?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // PECR consent for SMS. Only rendered when the block config asks for it,
  // and defaults OFF (opt-in must be explicit — a pre-ticked box wouldn't
  // count as consent). Never sent to the API when the checkbox is off.
  const [smsConsent, setSmsConsent] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === "sending") return;
    pushCtaClick("subscribe");
    // noValidate on the form means a malformed address reaches us instead of
    // being silently blocked by the browser's own type="email" validation,
    // otherwise this check (and the fail event below) would never run.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setState("error");
      return;
    }
    setState("sending");
    try {
      await joinMailingList(
        venueId,
        email.trim(),
        "site-pages",
        collectPhone && phone.trim() ? phone.trim() : undefined,
        listId,
        undefined,
        collectSmsConsent && smsConsent,
      );
      pushFormInteraction("success");
      setState("done");
      setEmail("");
      setPhone("");
      setSmsConsent(false);
    } catch {
      pushFormInteraction("fail");
      setState("error");
    }
  };
  const errorMsg = state === "error" && <p className="mt-3 text-xs" style={{ color: "#e05252" }}>Couldn't subscribe, try again.</p>;

  // Image variant: same editorial split as imageText/hours/faq, on the
  // venue's normal dark background, no inverted white panel.
  if (imageUrl) {
    return (
      <ImageContentSplit imageUrl={imageUrl} flip={flip} imageAspect="16/10" paddingClassName="pt-14 sm:pt-16 pb-3 sm:pb-4">
        <SectionHeading heading={heading || `Keep up with ${venueName}`} />
        {sub && <p className="mt-2 max-w-xl text-[15px]" style={{ color: MUTED }}>{sub}</p>}
        {state === "done" ? (
          <p className="mt-5 text-sm font-bold uppercase tracking-[0.22em]">You're on the list.</p>
        ) : (
          <form onSubmit={submit} noValidate className="mt-5 max-w-md">
            <div className="flex overflow-hidden rounded-lg border-2" style={{ borderColor: "var(--accent)" }}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email address"
                className="min-w-0 flex-1 border-0 bg-transparent px-4 py-3 text-sm outline-none placeholder:opacity-50"
                style={{ color: "var(--venue-text)" }}
              />
              <button type="submit" disabled={state === "sending"} className="border-0 px-6 text-[9px] font-bold uppercase tracking-[0.26em] transition hover:opacity-85 disabled:opacity-50" style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}>
                {state === "sending" ? "…" : "Join"}
              </button>
            </div>
            {collectPhone && (
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Mobile (optional, for tickets by SMS)"
                aria-label="Mobile number"
                className="mt-2 w-full rounded-lg border-2 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:opacity-50"
                style={{ color: "var(--venue-text)", borderColor: "var(--accent)" }}
              />
            )}
            {collectSmsConsent && (
              <label className="mt-2 flex cursor-pointer items-start gap-2 text-[12px]" style={{ color: MUTED }}>
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5"
                />
                <span>Text me about tickets, guestlist and last-minute drops. Reply STOP any time.</span>
              </label>
            )}
          </form>
        )}
        {errorMsg}
      </ImageContentSplit>
    );
  }

  return (
    // Inverted panel, the Certs trick: flip the palette for the capture.
    <section className="px-6 py-10 text-center sm:py-12" style={{ backgroundColor: "var(--venue-text)", color: "var(--venue-bg)" }}>
      <h2 className="font-extrabold uppercase tracking-tight" style={{ ...DISPLAY, fontSize: "clamp(1.3rem, 2.6vw, 1.9rem)" }}>
        {heading || `Keep up with ${venueName}`}
      </h2>
      {sub && <p className="mx-auto mt-2 max-w-xl text-[15px] opacity-70">{sub}</p>}
      {state === "done" ? (
        <p className="mt-5 text-sm font-bold uppercase tracking-[0.22em]">You're on the list.</p>
      ) : (
        <form onSubmit={submit} noValidate className="mx-auto mt-5 max-w-md">
          <div className="flex overflow-hidden rounded-lg border-2" style={{ borderColor: "var(--venue-bg)" }}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              aria-label="Email address"
              className="min-w-0 flex-1 border-0 bg-transparent px-4 py-3 text-sm outline-none placeholder:opacity-50"
              style={{ color: "var(--venue-bg)" }}
            />
            <button type="submit" disabled={state === "sending"} className="border-0 px-6 text-[9px] font-bold uppercase tracking-[0.26em] transition hover:opacity-85 disabled:opacity-50" style={{ backgroundColor: "var(--venue-bg)", color: "var(--venue-text)" }}>
              {state === "sending" ? "…" : "Join"}
            </button>
          </div>
          {collectPhone && (
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile (optional, for tickets by SMS)"
              aria-label="Mobile number"
              className="mt-2 w-full rounded-lg border-2 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:opacity-50"
              style={{ color: "var(--venue-bg)", borderColor: "var(--venue-bg)" }}
            />
          )}
          {collectSmsConsent && (
            <label className="mt-2 flex cursor-pointer items-start justify-center gap-2 text-[12px] opacity-80">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5"
              />
              <span>Text me about tickets, guestlist and last-minute drops. Reply STOP any time.</span>
            </label>
          )}
        </form>
      )}
      {errorMsg}
    </section>
  );
}
